import React, {useState} from 'react';
import {Alert, Button, Group, Stack, Text} from '@mantine/core';
import {closeModal, openModal} from '@mantine/modals';
import {notifications} from '@mantine/notifications';
import {startRegistration, type PublicKeyCredentialCreationOptionsJSON} from '@simplewebauthn/browser';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {PasskeyService} from '../../client';
import {formatRequestError} from '../../utils/formatRequestError';

type PasskeySetupProps = {
    /** Show status on /passkey even when a passkey already exists */
    forceVisible?: boolean;
};

export function PasskeySetup({forceVisible = false}: PasskeySetupProps) {
    const [registering, setRegistering] = useState(false);
    const [removing, setRemoving] = useState(false);
    const queryClient = useQueryClient();
    const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

    const {data, isLoading, isError} = useQuery({
        queryKey: ['passkey-status'],
        queryFn: PasskeyService.passkeyStatusApiV1PasskeyStatusGet,
    });

    const registerPasskey = async () => {
        if (!passkeySupported) {
            notifications.show({
                color: 'yellow',
                title: 'Passkey unavailable',
                message: 'Passkeys require HTTPS (e.g. mkcert for local development).',
            });
            return;
        }
        setRegistering(true);
        try {
            const optionsResponse = await PasskeyService.passkeyRegisterOptionsApiV1PasskeyRegisterOptionsPost();
            const credential = await startRegistration({
                optionsJSON: optionsResponse.options as unknown as PublicKeyCredentialCreationOptionsJSON,
            });
            await PasskeyService.passkeyRegisterVerifyApiV1PasskeyRegisterVerifyPost({
                credential: credential as unknown as Record<string, unknown>,
                challenge_token: optionsResponse.challenge_token,
            });
            await queryClient.invalidateQueries({queryKey: ['passkey-status']});
            notifications.show({
                color: 'green',
                title: 'Passkey set up',
                message: 'From now on, sign-in is passkey-only.',
            });
        } catch (err: unknown) {
            notifications.show({
                color: 'red',
                title: 'Registration failed',
                message: formatRequestError(
                    err,
                    'Could not register passkey. Check HTTPS, origin, and PKC_WEBAUTHN_* (see README).',
                ),
            });
        } finally {
            setRegistering(false);
        }
    };

    const removePasskey = async () => {
        setRemoving(true);
        try {
            await PasskeyService.passkeyDeleteApiV1PasskeyDelete();
            await queryClient.invalidateQueries({queryKey: ['passkey-status']});
            notifications.show({
                color: 'green',
                title: 'Passkey removed',
                message: 'You can sign in with your password again. Register a new passkey below when ready.',
            });
        } catch (err: unknown) {
            notifications.show({
                color: 'red',
                title: 'Could not remove passkey',
                message: formatRequestError(err, 'Removing the passkey failed.'),
            });
        } finally {
            setRemoving(false);
            closeModal('removePasskey');
        }
    };

    const showRemovePasskeyModal = () => {
        openModal({
            modalId: 'removePasskey',
            title: 'Remove passkey',
            children: (
                <Stack gap="md">
                    <Text size="sm">
                        Remove your passkey? You will sign in with your password until you register a
                        new passkey.
                    </Text>
                    <Group>
                        <Button color="red" loading={removing} onClick={() => removePasskey()}>
                            Yes, remove
                        </Button>
                        <Button variant="default" onClick={() => closeModal('removePasskey')}>
                            Cancel
                        </Button>
                    </Group>
                </Stack>
            ),
        });
    };

    if (isLoading) {
        return null;
    }

    if (isError) {
        return (
            <Alert color="red" title="Passkey status unavailable" mb="md">
                Backend endpoint /api/v1/passkey/status is unreachable. Is the API running with
                passkey support?
            </Alert>
        );
    }

    if (data?.has_passkey) {
        if (!forceVisible) {
            return null;
        }
        return (
            <Alert color="green" title="Passkey active" mb="md">
                <Stack gap="sm">
                    <Text size="sm">
                        A passkey is registered for your account. Sign-in is passkey-only. To use a
                        different device or authenticator, remove this passkey and register again.
                    </Text>
                    <Group>
                        <Button
                            color="red"
                            variant="light"
                            loading={removing}
                            onClick={showRemovePasskeyModal}
                        >
                            Remove passkey
                        </Button>
                    </Group>
                </Stack>
            </Alert>
        );
    }

    return (
        <Alert color="orange" title="Set up passkey" mb="md">
            <Stack gap="sm">
                <Text size="sm">
                    1. Sign in with <strong>username and password</strong> first (as before).<br/>
                    2. Then click &quot;Register passkey&quot; here and follow your browser&apos;s
                    prompts (fingerprint, Face ID, security key, etc.).<br/>
                    3. After that, your account will be passkey-only for sign-in.
                </Text>
                {!passkeySupported && (
                    <Text size="sm" c="yellow">
                        Your browser does not offer passkeys over HTTP. Run the admin frontend with
                        HTTPS (see README, QR scanner / mkcert section).
                    </Text>
                )}
                {passkeySupported && (
                    <Text size="xs" c="dimmed">
                        Current origin: {window.location.origin}.{' '}
                        <code>PKC_WEBAUTHN_ALLOWED_ORIGINS</code> must include this value;{' '}
                        <code>PKC_WEBAUTHN_RP_ID</code> must be the hostname (e.g. localhost or
                        127.0.0.1, without port).
                    </Text>
                )}
                <Group>
                    <Button
                        onClick={registerPasskey}
                        loading={registering}
                        disabled={!passkeySupported}
                    >
                        Register passkey
                    </Button>
                </Group>
            </Stack>
        </Alert>
    );
}
